import { useState } from "react";
import { CheckSquare } from "lucide-react";
import { ChecklistItemCard } from "./ChecklistItemCard";
import { completionColor, completionBarColor } from "./chatCardUtils";
import type { ChecklistResult } from "./chatCardUtils";

const MAX_ITEMS_PER_CATEGORY = 3;

interface ChecklistProgressCardProps {
  data: ChecklistResult;
}

export function ChecklistProgressCard({ data }: ChecklistProgressCardProps) {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(),
  );

  const overallPercent =
    data.totalItems > 0
      ? Math.round((data.completedItems / data.totalItems) * 100)
      : 0;

  const toggleCategory = (catId: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(catId)) {
        next.delete(catId);
      } else {
        next.add(catId);
      }
      return next;
    });
  };

  return (
    <div className="mt-2 space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted uppercase tracking-wider">
          <CheckSquare size={12} />
          <span>Checklist Progress</span>
        </div>
        <span
          className={`text-xs font-semibold ${completionColor(overallPercent)}`}
        >
          {data.completedItems}/{data.totalItems} ({overallPercent}%)
        </span>
      </div>

      {/* Overall progress bar */}
      <div className="w-full h-2 rounded-full bg-border/30 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${completionBarColor(overallPercent)}`}
          style={{ width: `${overallPercent}%` }}
        />
      </div>

      {/* Category breakdown */}
      <div className="space-y-2">
        {data.categories.map((category) => {
          const catPercent =
            category.totalCount > 0
              ? Math.round(
                  (category.completedCount / category.totalCount) * 100,
                )
              : 0;
          const isExpanded = expandedCategories.has(category.id);
          const visibleItems = isExpanded
            ? category.items
            : category.items.slice(0, MAX_ITEMS_PER_CATEGORY);
          const remaining = category.items.length - MAX_ITEMS_PER_CATEGORY;

          return (
            <div
              key={category.id}
              className="rounded-lg border border-border/40 bg-card p-2 space-y-1.5"
            >
              {/* Category header */}
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-primary truncate">
                  {category.name}
                </span>
                <span
                  className={`text-[10px] font-semibold ${completionColor(catPercent)}`}
                >
                  {category.completedCount}/{category.totalCount}
                </span>
              </div>

              {/* Thin progress bar */}
              <div className="w-full h-1 rounded-full bg-border/30 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${completionBarColor(catPercent)}`}
                  style={{ width: `${catPercent}%` }}
                />
              </div>

              {/* Items */}
              {visibleItems.length > 0 && (
                <div className="space-y-1">
                  {visibleItems.map((item) => (
                    <ChecklistItemCard key={item.id} item={item} />
                  ))}
                </div>
              )}

              {/* Show more toggle */}
              {remaining > 0 && !isExpanded && (
                <button
                  type="button"
                  onClick={() => toggleCategory(category.id)}
                  className="text-[11px] text-accent hover:text-accent/80 transition-colors font-medium"
                >
                  +{remaining} more items
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
